import { useEffect, useState } from "react";
import ModalComponent from "components/modal";
import { useSharedState } from "state";
import Table from "components/table";
import ActionButton from "components/table/action-btn";
import { oneLine } from "components/table/table-parts";
import { IconCheck, IconLock, IconLockOpen, IconPencil, IconX } from "@tabler/icons-react";
import WarningModal from "components/modal/warning";
import { addDepartment, updateDepartment } from "api/departments";
import { Department, DepartmentDefault } from "types/dict";

export default function Departments() {
  const { departments, getDepartmentsData, notify } = useSharedState();

  const [departmentToChange, setDepartmentToChange] = useState<Department>(DepartmentDefault);
  const [editingDepartment, setEditingDepartment] = useState<number>(0);
  const [isAddingDepartment, setAddingDepartment] = useState<boolean>(false);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);

  const [newDepartmentName, setNewDepartmentName] = useState<string>("");

  const [changeDepartmentDanger, setDepartmentDanger] = useState<string>("");

  useEffect(() => {
    getDepartmentsData();
  }, []);

  const openDepartmentModal = () => {
    const button = document.createElement("button");
    button.setAttribute("data-bs-toggle", "modal");
    button.setAttribute("data-bs-target", "#modal");
    document.body.appendChild(button);
    button.click();
    button.remove();
  };

  const cancelDepartmentChange = () => {
    setDepartmentToChange(DepartmentDefault);
    setEditingDepartment(0);
    setAddingDepartment(false);

    setNewDepartmentName("");
    setDepartmentDanger("");
  };

  const onDepartmentEdit = (item: Department) => {
    setEditingDepartment(item.department_id);
    setDepartmentToChange(item);
    setAddingDepartment(false);

    setNewDepartmentName(item.department);
    setDepartmentDanger("Вы действительно хотите отредактировать отдел?");
  };

  const onDepartmentBlock = (item: Department) => {
    setDepartmentToChange(item);
    setEditingDepartment(0);
    setAddingDepartment(false);

    setNewDepartmentName("");
    setDepartmentDanger("Вы действительно хотите деактивировать отдел?");
  };

  const onDepartmentCreate = () => {
    setEditingDepartment(0);
    setAddingDepartment(true);

    setNewDepartmentName("");
    setDepartmentDanger("");
  };

  const changeDepartment = async () => {
    openDepartmentModal();
    setFormLoading(true);

    try {
      await updateDepartment(departmentToChange.department_id, newDepartmentName, departmentToChange.status);
      notify("Отдел изменён", "success");

      cancelDepartmentChange();
      await getDepartmentsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при изменении отдела");
    } finally {
      setFormLoading(false);
    }
  };

  const changeDepartmentStatus = async () => {
    openDepartmentModal();
    setFormLoading(true);

    try {
      await updateDepartment(
        departmentToChange.department_id,
        departmentToChange.department,
        !departmentToChange.status
      );
      notify("Статус изменён", "success");

      cancelDepartmentChange();
      await getDepartmentsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при изменении статуса");
    } finally {
      setFormLoading(false);
    }
  };

  const createDepartment = async () => {
    setFormLoading(true);

    try {
      await addDepartment(newDepartmentName);
      notify("Отдел добавлен", "success");

      cancelDepartmentChange();
      await getDepartmentsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при добавлении отдела");
    } finally {
      setFormLoading(false);
    }
  };

  const departmentsTableHeaders = [
    { header_text: "Название", narrow: false, wrap: false },
    { header_text: "Статус", narrow: false, wrap: false },
    { header_text: "Действия", narrow: false, wrap: false },
  ];

  const departmentsList = (
    <>
      {departments.map((department, index) => (
        <tr key={index}>
          {editingDepartment === department.department_id ? (
            <>
              <td>
                <input
                  type="text"
                  className="form-control rounded-2xl"
                  placeholder="Название"
                  value={newDepartmentName}
                  onChange={(e) => setNewDepartmentName(e.target.value.slice(0, 100))}
                  disabled={isFormLoading}
                />
              </td>
              <td></td>
              <td>
                <div className="btn-list flex-nowrap">
                  <ActionButton
                    icon={<IconCheck className="icon" />}
                    title="Сохранить"
                    action={() => undefined}
                    color="primary"
                    modal={true}
                    modal_id="#modal-change-department"
                  />
                  <ActionButton
                    icon={<IconX className="icon" />}
                    title="Отмена"
                    action={cancelDepartmentChange}
                    color="danger"
                    modal={false}
                  />
                </div>
              </td>
            </>
          ) : (
            <>
              <td>{oneLine(department.department)}</td>
              <td>
                {oneLine(department.status ? "Активный" : "Неактивный", department.status ? "primary" : "danger")}
              </td>
              <td>
                <div className="btn-list flex-nowrap">
                  <ActionButton
                    icon={<IconPencil className="icon" />}
                    title={"Изменить"}
                    action={() => onDepartmentEdit(department)}
                    color={"primary"}
                    modal={false}
                  />
                  <ActionButton
                    icon={department.status ? <IconLock className="icon" /> : <IconLockOpen className="icon" />}
                    title={`${department.status ? "Деактивировать" : "Активировать"}`}
                    action={() => onDepartmentBlock(department)}
                    color={`${department.status ? "danger" : "primary"}`}
                    modal={true}
                    modal_id="#modal-change-department"
                  />
                </div>
              </td>
            </>
          )}
        </tr>
      ))}

      {isAddingDepartment && (
        <tr>
          <td>
            <input
              type="text"
              className="form-control"
              placeholder="Название нового отдела"
              value={newDepartmentName}
              onChange={(e) => setNewDepartmentName(e.target.value.slice(0, 100))}
              disabled={isFormLoading}
            />
          </td>
          <td></td>
          <td>
            <ActionButton
              icon={<IconX className="icon" />}
              title="Отмена"
              action={cancelDepartmentChange}
              color="danger"
              modal={false}
            />
          </td>
        </tr>
      )}
    </>
  );

  const usersTable = Table(departmentsTableHeaders, departmentsList, false);

  const departmentsFooter = (
    <button
      className="btn btn-primary d-flex gap-1"
      onClick={isAddingDepartment ? createDepartment : onDepartmentCreate}
      disabled={isFormLoading || (isAddingDepartment && !newDepartmentName?.length)}
    >
      {isAddingDepartment ? "Сохранить" : "Добавить отдел"}
      {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
    </button>
  );

  return (
    <>
      <ModalComponent
        header="Список отделов"
        body={usersTable}
        footer={departmentsFooter}
        disableClose={isFormLoading}
      />
      <WarningModal
        modal_id="modal-change-department"
        warningDescription={changeDepartmentDanger}
        cancel={openDepartmentModal}
        confirm={editingDepartment ? changeDepartment : changeDepartmentStatus}
      />
    </>
  );
}
